import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaFileExport, FaSms } from "react-icons/fa";
import { toast } from "react-toastify";
import Server from "../../Shared/Server";
import classnames from 'classnames'
import Spinner from "../../Shared/Spinner";
import SelectField from "../../Shared/SelectField";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const XMonthsUnPaid = () => {
  const current_user = JSON.parse(localStorage.getItem('user'))
  const billing_date  = current_user?.branch?.billing_date

  const [data, setData] = useState([]);
  const [errors, setErrors] = useState({});
  const [title, setTitle] = useState('');
  const [xOptions, setXOptions] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const [formData, setFormData] = useState({
    x: location.state?.x || 2,
    year: location.state?.year || billing_date.year,
    month: location.state?.month || billing_date.month,
  })
  const {x, year, month} = formData
  const x_list = [
    {id: 2, name: "2 Months", key: 2},
    {id: 3, name: "3 Months", key: 3},
    {id: 4, name: "4 Months", key: 4},
    {id: 5, name: "5 Months", key: 5},
  ]

  const gridRef = useRef();
  const navigate = useNavigate();

  // Fetch API data
  const fetchData = async (payload) => {

    setIsLoading(true);
    try {
      const response = await Server.post("/un-paid/x-months", {
        year: year,
        month: month,
        x: payload.x
      });
      const res = response.data;
      setData(res.data);

      // Extract month keys from the "months" array.
      const monthsFromApi = res.months.map((item) => Object.keys(item)[0]);

      // Build column definitions dynamically
      buildColumnDefs(monthsFromApi);

      // Compute totals for pinned bottom row.
      computePinnedTotals(res.data, monthsFromApi);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false)
      if(error?.response?.status === 401){
          navigate('/login')
      }else if (error?.response?.status === 422){
          setErrors({})
          error.response?.data?.detail.map((err) => (
              setErrors((prevState) => ({
                  ...prevState,
                  [err.loc[1]]: err.msg
              }))
          ))
      }else if (error?.response?.status === 403){
          toast.error(error?.response?.data?.detail || 'Forbidden');
          navigate('/');
      }else if (error?.response?.status === 402){
        toast.error(error?.response?.data?.detail || 'Payment required');
        navigate('/')
      }else {
          toast.error(error?.response?.data?.detail || 'Failed to load X months unpaid data');
      }
    }
  };

  // Build column definitions dynamically using monthList
  const buildColumnDefs = (monthKeys) => {
    // Static columns:
    let staticCols = [
      {
        headerName: "#",
        field: "index",
        maxWidth: 70,
        sortable: true,
        filter: false,
        valueFormatter: (params) =>
          isNaN(params.node.rowIndex) ? "" : `${params.node.rowIndex + 1}`,
      },
      {
        headerName: "Actions",
        pinned: 'left',
        minWidth: 80,
        sortable: false,
        filter: false,
        cellRenderer: (e) => {
            if (!e.data) return null;
            if (e.node.rowPinned) return '';
            const isDisabled = e.data.total_bill < 1;
            return (
                <div className="d-flex gap-1">
                    <Link
                        className={classnames('btn btn-outline-info', {'disabled': isDisabled})}
                        title={isDisabled ? "No bill to send SMS for" : "Send SMS to unpaid customers"}
                        to="/send-x-months-unpaid-message"
                        state={{ payload: {
                            public_id: e.data.public_id,
                            year: year,
                            month: month,
                            x: x,
                        } }}
                        onClick={(ev) => { if (isDisabled) ev.preventDefault(); }}
                    >
                        <FaSms/>
                    </Link>
                </div>
            );
        },
      },
      {
        headerName: "Name",
        field: "name",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Locations",
        field: "locations",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Previous Balance",
        field: "previous_balance",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total Bill",
        field: "total_bill",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Total",
        field: "total",
        sortable: true,
        filter: "agNumberColumnFilter",
      },
    ];

    // Dynamic month columns:
    const dynamicCols = monthKeys.map((month) => ({
      headerName: month,
      field: month,
      sortable: true,
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        const val = parseFloat(params.value);
        return isNaN(val) ? "0.00" : val.toFixed(2);
      },
    }));

    // Find the index of the "previous_balance" column
    const previousBalanceIndex = staticCols.findIndex(col => col.field === "previous_balance");

    // Combine the columns so that dynamicColumns appear right after "PreviousBalance column"
    const finalColumnDefs = [
        ...staticCols.slice(0, previousBalanceIndex + 1),
        ...dynamicCols,
        ...staticCols.slice(previousBalanceIndex + 1),
    ];

    setColumnDefs(finalColumnDefs);
  };

  // Compute totals for pinned bottom row based on data and monthList
  const computePinnedTotals = (dataRecords, monthKeys) => {
    // Initialize totals for static columns:
    let totals = {
      locations: 0,
      previous_balance: 0,
      total_bill: 0,
      total: 0,
    };

    // And for dynamic month columns:
    monthKeys.forEach((month) => {
      totals[month] = 0;
    });

    dataRecords.forEach((record) => {
      totals.locations += parseInt(record.locations) || 0;
      totals.previous_balance += parseFloat(record.previous_balance || 0);
      totals.total_bill += parseFloat(record.total_bill || 0);
      totals.total += parseFloat(record.total || 0);

      monthKeys.forEach((month) => {
        totals[month] += parseFloat(record[month] || 0);
      });
    });

    const formattedTotals = {};
    Object.keys(totals).forEach(key => {
        if (key === "locations"){
            formattedTotals[key] = Number(totals[key]);
        }else {
            formattedTotals[key] = Number(totals[key]).toFixed(2);
        }
    });

    formattedTotals.name = "Totals";

    setPinnedBottomRowData([formattedTotals]);
  };

  useEffect(() => {

    const options = []
    setTitle(`${x} Months Upaid`);
    x_list.map(x => (
      options.push({
        id: x.id,
        key: x.key,
        name: x.name
      })
    ));

    setXOptions(options)
    fetchData({x: x});
  }, []);

  const onChange = (e) => {
    setFormData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value
    }))
  }

  const onDoubleClick = (payload) => {
    navigate(`/x-months-un-paid-detail`, { state: {payload: payload}})
  }

  const onSubmit = (e) => {
    e.preventDefault()

    fetchData({x: x})
    setTitle(`${x} Months Upaid`);
  }


  const defaultColDef = useMemo(() => {
    return {
      filterParams: {
        buttons: [],
      },
    };
  }, []);

  const onFirstDataRendered = (params) => {
    params.api.autoSizeAllColumns();
  }

  const onGridSizeChanged = (params) => {
    params.api.autoSizeAllColumns();
  }

  const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    };

  const getRowStyle = (params) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        color: "#000",
      };
    }
    return {};
  };

  if (isLoading) return <Spinner />;

  return (
        <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="x"
                        value={parseInt(x)}
                        options={xOptions}
                        onChange={onChange}
                        error={errors.x}
                    />
                </div>
                <div className="col-md-6">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-4">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                              if (e.node.rowPinned) return '';
                              else if (e.data.total_bill < 1) return '';
                              return onDoubleClick({
                                  x: x,
                                  year: parseInt(year),
                                  month: parseInt(month),
                                  public_id: e.data.public_id,
                                  name: e.data.name
                              })
                            }}
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}
                            rowData={data}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  );
};

export default XMonthsUnPaid;
